import * as React from "react";
import { Card, CardProps, Box, useMediaQuery } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import WebGLGlassBackground from "./WebGLGlassBackground";

export interface GlassCardProps extends CardProps {
    children: React.ReactNode;
    effect?: "clear" | "regular" | "none";
    interactive?: boolean;
    colorScheme?: "light" | "dark" | "system";
    hoverable?: boolean; // Keep for backward compatibility
    gradient?: boolean; // Keep for backward compatibility
    glassIntensity?: "light" | "regular" | "strong"; // Keep for backward compatibility
    nested?: boolean; // For nested cards that shouldn't blur parent
    useWebGL?: boolean; // Use WebGL glass background effect
    webGLSourceImage?: string; // Source image for WebGL texture
    webGLFragmentShader?: string; // Custom fragment shader
    performanceMode?: "auto" | "lightweight" | "full"; // Performance optimization mode
    disableLiquidEffect?: boolean; // Disable liquid glass displacement map effect (use simple blur only)
    useSimpleGlass?: boolean; // Use simple glass effect instead of complex one
}

// Constants moved outside component to avoid recalculation
// Based on advanced displacement map glassmorphism
const GLASS_CONSTANTS = {
    frost: 0.18, // Default frost opacity - increased for better text visibility
    saturation: 1.5,
    displace: 0.2, // Output blur for displacement
    scale: -180,
    blur: 11, // Input blur (full mode)
    blurLightweight: 6, // Reduced blur for lightweight mode
    lightness: 50,
    alpha: 0.93,
    border: 0.07,
    blend: "difference" as const,
    xChannel: "R" as const,
    yChannel: "B" as const,
} as const;

// Pre-computed white shadow values
const WHITE_SHADOWS = {
    low: "rgba(255, 255, 255, 0.05)",
    medium: "rgba(255, 255, 255, 0.08)",
    high: "rgba(255, 255, 255, 0.10)",
    hover: "rgba(255, 255, 255, 0.12)",
    hoverInset: "rgba(255, 255, 255, 0.15)",
    hoverInsetStrong: "rgba(255, 255, 255, 0.20)",
    hoverInsetStronger: "rgba(255, 255, 255, 0.25)",
} as const;

// Pre-computed transition string
const TRANSITION = `
  background-color 400ms cubic-bezier(1, 0.0, 0.4, 1),
  box-shadow 400ms cubic-bezier(1, 0.0, 0.4, 1),
  transform 400ms cubic-bezier(1, 0.0, 0.4, 1),
  opacity 260ms ease-out
`.trim();

// Memoized SVG Filter Component to avoid re-renders
const SVGFilter = React.memo<{ filterId: string; displacementSvg: string; }>(({ filterId, displacementSvg }) => (
    <svg width="0" height="0" style={{ position: "absolute", pointerEvents: "none" }}>
        <defs>
            <filter id={filterId} x="0%" y="0%" width="100%" height="100%">
                <feGaussianBlur in="SourceGraphic" stdDeviation={GLASS_CONSTANTS.displace} />
                <feImage
                    href={`data:image/svg+xml,${displacementSvg}`}
                    result="displacement"
                />
                <feDisplacementMap
                    in="SourceGraphic"
                    in2="displacement"
                    scale={GLASS_CONSTANTS.scale}
                    xChannelSelector={GLASS_CONSTANTS.xChannel}
                    yChannelSelector={GLASS_CONSTANTS.yChannel}
                />
            </filter>
        </defs>
    </svg>
));
SVGFilter.displayName = "SVGFilter";

/**
 * GlassCard component that provides glassmorphism effect similar to iOS Liquid Glass
 * If effect is 'none', returns simple Card without glass effect to preserve backgroundColor
 */
const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(({
    children,
    effect,
    interactive = true,
    colorScheme = "system",
    hoverable = false,
    gradient = false,
    glassIntensity, // For backward compatibility
    nested = false,
    useWebGL = false,
    webGLSourceImage,
    webGLFragmentShader,
    performanceMode = "auto", // auto = detect mobile, lightweight = always lightweight, full = always full
    disableLiquidEffect = false, // Disable liquid glass displacement map for better performance
    useSimpleGlass = false, // Use simple glass effect
    sx,
    className,
    ...props
}, ref) => {
    const theme = useTheme();
    const systemIsDark = theme.palette.mode === "dark";
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Determine if we should use lightweight mode
    const useLightweight = React.useMemo(() => {
        if (performanceMode === "lightweight") return true;
        if (performanceMode === "full") return false;
        // auto mode: use lightweight on mobile
        return isMobile;
    }, [performanceMode, isMobile]);

    // Determine if liquid effect should be disabled
    const useLiquidEffect = React.useMemo(() => {
        // Disable if explicitly disabled or in lightweight mode
        return !disableLiquidEffect && !useLightweight;
    }, [disableLiquidEffect, useLightweight]);

    // Disable WebGL on mobile automatically
    const effectiveUseWebGL = React.useMemo(() => {
        return useWebGL && !isMobile;
    }, [useWebGL, isMobile]);

    // Memoize color scheme determination
    const isDark = React.useMemo(
        () => colorScheme === "system" ? systemIsDark : colorScheme === "dark",
        [colorScheme, systemIsDark]
    );

    // Memoize resolved effect (map old glassIntensity to new effect prop for backward compatibility)
    const resolvedEffect = React.useMemo(
        () => effect || (glassIntensity === "light" ? "clear" : glassIntensity === "strong" ? "regular" : "regular"),
        [effect, glassIntensity]
    );

    // Generate unique filter ID for each card instance (must be before any early returns)
    // Using useRef instead of useId for React 17 compatibility
    const filterIdRef = React.useRef<string | null>(null);
    if (!filterIdRef.current) {
        filterIdRef.current = `glass-filter-${Math.random().toString(36).substr(2, 9)}`;
    }
    const filterId = filterIdRef.current;

    // Get card dimensions for displacement map (only needed in full mode)
    const cardRef = React.useRef<HTMLDivElement | null>(null);
    const [cardDimensions, setCardDimensions] = React.useState({ width: 336, height: 96 });
    const rafRef = React.useRef<number | null>(null);
    const lastDimensionsRef = React.useRef({ width: 336, height: 96 });

    // Throttled callback to update dimensions (only update if significant change)
    // DISABLED in lightweight mode or if liquid effect is disabled to save performance
    const updateDimensions = React.useCallback(() => {
        // Skip dimension tracking if liquid effect is disabled (no displacement map needed)
        if (!useLiquidEffect) return;

        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
        }
        rafRef.current = requestAnimationFrame(() => {
            if (cardRef.current) {
                const rect = cardRef.current.getBoundingClientRect();
                const newWidth = Math.max(rect.width || 336, 80);
                const newHeight = Math.max(rect.height || 96, 35);

                // Increased threshold to 50px for better performance (less frequent updates)
                const threshold = 50;
                const widthDiff = Math.abs(newWidth - lastDimensionsRef.current.width);
                const heightDiff = Math.abs(newHeight - lastDimensionsRef.current.height);

                if (widthDiff > threshold || heightDiff > threshold) {
                    lastDimensionsRef.current = { width: newWidth, height: newHeight };
                    setCardDimensions({ width: newWidth, height: newHeight });
                }
            }
        });
    }, [useLiquidEffect]);

    // Update card dimensions on mount and resize with throttled ResizeObserver
    // DISABLED in lightweight mode or if liquid effect is disabled to save performance
    React.useEffect(() => {
        // Skip ResizeObserver if liquid effect is disabled (no displacement map needed)
        if (!useLiquidEffect) return;

        if (cardRef.current) {
            const rect = cardRef.current.getBoundingClientRect();
            const initialWidth = Math.max(rect.width || 336, 80);
            const initialHeight = Math.max(rect.height || 96, 35);
            lastDimensionsRef.current = { width: initialWidth, height: initialHeight };
            setCardDimensions({ width: initialWidth, height: initialHeight });
        }

        // Increased debounce to 300ms for better performance
        let resizeTimeout: NodeJS.Timeout;
        const debouncedUpdate = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(updateDimensions, 300);
        };

        const resizeObserver = new ResizeObserver(debouncedUpdate);
        const currentRef = cardRef.current;
        if (currentRef) {
            resizeObserver.observe(currentRef);
        }

        return () => {
            resizeObserver.disconnect();
            clearTimeout(resizeTimeout);
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [updateDimensions, useLiquidEffect]);

    // Memoize glass configuration with optimized shadows (must be before early return)
    const glassConfig = React.useMemo(() => {
        const { frost, saturation } = GLASS_CONSTANTS;
        const blurAmount = useLightweight ? GLASS_CONSTANTS.blurLightweight : GLASS_CONSTANTS.blur;
        const saturationAmount = useLightweight ? 1.5 : saturation;

        // Lightweight mode: minimal shadows (1 layer only) - NO inset shadows
        // Add stronger shadow for visibility on white background
        if (useLightweight) {
            const baseShadow = isDark
                ? `0 8px 24px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2)`
                : `0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08)`;

            return {
                clear: {
                    blur: `blur(${blurAmount}px) saturate(${saturationAmount})`,
                    background: isDark
                        ? `hsl(0 0% 0% / ${frost})`
                        : `hsl(0 0% 100% / ${frost})`,
                    boxShadow: baseShadow, // Stronger shadow for visibility
                },
                regular: {
                    blur: `blur(${blurAmount}px) saturate(${saturationAmount})`,
                    background: isDark
                        ? `hsl(0 0% 0% / ${frost + 0.05})`
                        : `hsl(0 0% 100% / ${frost + 0.05})`,
                    boxShadow: isDark
                        ? `0 8px 24px rgba(0, 0, 0, 0.35), 0 4px 12px rgba(0, 0, 0, 0.25)`
                        : `0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)`, // Stronger shadow for visibility
                },
            };
        }

        // Full mode: reduced shadows (3-4 layers instead of 8) - still expensive but better
        // Add stronger shadow for visibility on white background
        const darkShadow = `
      0px 8px 24px rgba(0, 0, 0, 0.3),
      0px 16px 56px rgba(0, 0, 0, 0.25),
      0px 4px 16px rgba(0, 0, 0, 0.2) inset,
      0px 8px 24px rgba(0, 0, 0, 0.15) inset
    `.trim();

        const lightShadow = `
      0px 8px 24px rgba(0, 0, 0, 0.12),
      0px 16px 56px rgba(0, 0, 0, 0.08),
      0px 4px 16px rgba(0, 0, 0, 0.06) inset,
      0px 8px 24px rgba(0, 0, 0, 0.04) inset
    `.trim();

        return {
            clear: {
                blur: `blur(${blurAmount}px) saturate(${saturationAmount})`,
                background: isDark
                    ? `hsl(0 0% 0% / ${frost})`
                    : `hsl(0 0% 100% / ${frost})`,
                // Stronger shadow for better visibility on white background
                boxShadow: isDark ? darkShadow : lightShadow,
            },
            regular: {
                blur: `blur(${blurAmount + 1}px) saturate(${saturationAmount})`,
                background: isDark
                    ? `hsl(0 0% 0% / ${frost + 0.05})`
                    : `hsl(0 0% 100% / ${frost + 0.05})`,
                // Stronger shadow for better visibility on white background
                boxShadow: isDark
                    ? `
            0px 8px 24px rgba(0, 0, 0, 0.35),
            0px 16px 56px rgba(0, 0, 0, 0.3),
            0px 4px 16px rgba(0, 0, 0, 0.25) inset,
            0px 8px 24px rgba(0, 0, 0, 0.2) inset
          `.trim()
                    : `
            0px 8px 24px rgba(0, 0, 0, 0.15),
            0px 16px 56px rgba(0, 0, 0, 0.1),
            0px 4px 16px rgba(0, 0, 0, 0.08) inset,
            0px 8px 24px rgba(0, 0, 0, 0.06) inset
          `.trim(),
            },
        };
    }, [isDark, useLightweight]);

    // Type-safe config access
    const effectForConfig: "clear" | "regular" = resolvedEffect === "none" ? "clear" : resolvedEffect;
    const config = glassConfig[effectForConfig];

    // Memoize shouldHover to avoid recalculation
    const shouldHover = React.useMemo(
        () => hoverable || interactive,
        [hoverable, interactive]
    );

    // Round dimensions to reduce unnecessary SVG regenerations (must be before early return)
    // Round to 40px increments for even better performance
    // Only calculate if liquid effect is enabled
    const roundedDimensions = React.useMemo(() => {
        if (!useLiquidEffect) return { width: 336, height: 96 }; // Default values
        const width = Math.round(cardDimensions.width / 40) * 40;
        const height = Math.round(cardDimensions.height / 40) * 40;
        return { width, height };
    }, [cardDimensions.width, cardDimensions.height, useLiquidEffect]);

    // Build displacement SVG based on card dimensions (must be before early return)
    // Only regenerate if dimensions changed significantly and liquid effect is enabled
    const displacementSvg = React.useMemo(() => {
        // Return empty if liquid effect is disabled
        if (!useLiquidEffect) return "";
        const { border, lightness, alpha, blur, blend } = GLASS_CONSTANTS;
        const { width, height } = roundedDimensions;
        const borderSize = Math.min(width, height) * (border * 0.5);
        const radius = Math.min(16, Math.min(width, height) * 0.1); // Adaptive radius

        return encodeURIComponent(`
      <svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="red-${filterId}" x1="100%" y1="0%" x2="0%" y2="0%">
            <stop offset="0%" stop-color="#000"/>
            <stop offset="100%" stop-color="red"/>
          </linearGradient>
          <linearGradient id="blue-${filterId}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#000"/>
            <stop offset="100%" stop-color="blue"/>
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="${width}" height="${height}" fill="black"/>
        <rect x="0" y="0" width="${width}" height="${height}" rx="${radius}" fill="url(#red-${filterId})"/>
        <rect x="0" y="0" width="${width}" height="${height}" rx="${radius}" fill="url(#blue-${filterId})" style="mix-blend-mode: ${blend}"/>
        <rect x="${borderSize}" y="${borderSize}" width="${width - borderSize * 2}" height="${height - borderSize * 2}" rx="${radius}" fill="hsl(0 0% ${lightness}% / ${alpha})" style="filter:blur(${blur}px)"/>
      </svg>
    `.trim());
    }, [roundedDimensions.width, roundedDimensions.height, filterId, useLiquidEffect]);

    // Memoize className (must be before early return)
    const computedClassName = React.useMemo(
        () =>
            `glass-effect ${isDark ? "glass-effect-dark" : ""} glass-transition ${shouldHover ? "glass-hover" : ""
                } ${className || ""}`.trim(),
        [isDark, shouldHover, className]
    );

    // Memoize hover box shadow (must be before early return)
    const hoverBoxShadow = React.useMemo(() => {
        // Lightweight mode: stronger shadow for visibility
        if (useLightweight) {
            return isDark
                ? `0 12px 32px rgba(0, 0, 0, 0.4), 0 6px 16px rgba(0, 0, 0, 0.3)`
                : `0 12px 32px rgba(0, 0, 0, 0.18), 0 6px 16px rgba(0, 0, 0, 0.12)`;
        }
        // Full mode: stronger shadow for visibility
        return isDark
            ? `
        0px 8px 24px rgba(0, 0, 0, 0.4),
        0px 16px 56px rgba(0, 0, 0, 0.35),
        0px 8px 24px rgba(0, 0, 0, 0.3) inset
      `.trim()
            : `
        0px 8px 24px rgba(0, 0, 0, 0.2),
        0px 16px 56px rgba(0, 0, 0, 0.15),
        0px 8px 24px rgba(0, 0, 0, 0.12) inset
      `.trim();
    }, [useLightweight, isDark]);

    // Memoize breakpoint down function to avoid recreation (must be before nestedSx and mainSx)
    const breakpointDown = React.useMemo(
        () => theme.breakpoints.down("sm"),
        [theme.breakpoints]
    );

    // Memoize nested card sx with performance optimizations (must be before early return)
    const nestedSx = React.useMemo(
        () => ({
            position: "relative" as const,
            borderRadius: (theme: any) => theme.shape.borderRadius * 2,
            overflow: "hidden" as const,
            border: "none" as const,
            background: config.background,
            // Use same blur for both to avoid double rendering cost
            backdropFilter: config.blur,
            WebkitBackdropFilter: config.blur,
            boxShadow: config.boxShadow,
            transition: TRANSITION,
            // GPU acceleration optimizations
            transform: "translateZ(0)" as const,
            willChange: "transform, opacity" as const,
            backfaceVisibility: "hidden" as const,
            isolation: "isolate" as const,
            // CSS containment for performance optimization
            contain: "layout style paint" as const,
            // Optimize compositing
            transformStyle: "preserve-3d" as const,
            [breakpointDown]: {
                borderRadius: (theme: any) => theme.shape.borderRadius * 1.5,
            },
            ...(shouldHover && {
                cursor: "pointer" as const,
                "&:hover": {
                    transform: "translateY(-2px) translateZ(0)",
                    background: isDark
                        ? `hsl(0 0% 0% / ${GLASS_CONSTANTS.frost + 0.08})`
                        : `hsl(0 0% 100% / ${GLASS_CONSTANTS.frost + 0.08})`,
                    boxShadow: hoverBoxShadow,
                },
            }),
            ...sx,
        }),
        [config, shouldHover, hoverBoxShadow, breakpointDown, sx]
    );

    // Memoize content box sx
    const contentBoxSx = React.useMemo(
        () => ({
            position: "relative" as const,
            zIndex: 2,
            width: "100%",
            height: "100%",
        }),
        []
    );

    // Memoize backdrop filter string (NO displacement map in lightweight mode or if disabled)
    const backdropFilterString = React.useMemo(() => {
        if (!useLiquidEffect) {
            // Simple blur only - NO displacement map, NO brightness filter
            // This eliminates the expensive SVG filter pipeline
            const blurAmount = useLightweight ? GLASS_CONSTANTS.blurLightweight : GLASS_CONSTANTS.blur;
            return `blur(${blurAmount}px) saturate(${GLASS_CONSTANTS.saturation})`;
        }
        // Full mode: with displacement map (expensive but optional)
        return `url(#${filterId}) brightness(1.1) saturate(${GLASS_CONSTANTS.saturation})`;
    }, [filterId, useLightweight, useLiquidEffect]);

    // Memoize hover background
    const hoverBackground = React.useMemo(
        () => isDark
            ? `hsl(0 0% 0% / ${GLASS_CONSTANTS.frost + 0.08})`
            : `hsl(0 0% 100% / ${GLASS_CONSTANTS.frost + 0.08})`,
        [isDark]
    );

    // Memoize main card sx with displacement map filter (must be before early return)
    const mainSx = React.useMemo(
        () => ({
            position: "relative" as const,
            borderRadius: (theme: any) => theme.shape.borderRadius * 2,
            overflow: "hidden" as const,
            border: "none" as const,
            background: config.background,
            // Backdrop filter (with or without displacement map based on mode)
            // In lightweight: simple blur only (no double filter cost)
            // In full: displacement map + blur (expensive)
            backdropFilter: backdropFilterString,
            // WebkitBackdropFilter: use same as backdropFilter to avoid double rendering
            WebkitBackdropFilter: backdropFilterString,
            boxShadow: config.boxShadow,
            transition: TRANSITION,
            // GPU acceleration optimizations (reduced in lightweight mode)
            ...(useLightweight ? {} : {
                transform: "translateZ(0)" as const,
                willChange: "transform, opacity" as const,
                backfaceVisibility: "hidden" as const,
                isolation: "isolate" as const,
                contain: "layout style paint" as const,
                transformStyle: "preserve-3d" as const,
            }),
            [breakpointDown]: {
                borderRadius: (theme: any) => theme.shape.borderRadius * 1.5,
            },
            ...(shouldHover && {
                cursor: "pointer" as const,
                "&:hover": {
                    transform: "translateY(-2px)" + (useLightweight ? "" : " translateZ(0)"),
                    background: hoverBackground,
                    boxShadow: hoverBoxShadow,
                    // Disable GPU filter on hover in lightweight mode
                    ...(useLightweight ? { filter: "none" } : {}),
                },
            }),
            ...sx,
        }),
        [config, backdropFilterString, shouldHover, hoverBoxShadow, breakpointDown, hoverBackground, useLightweight, sx]
    );

    // Memoize original effect check (must be before early return)
    const originalEffect = React.useMemo(
        () => effect || (glassIntensity === "light" ? "clear" : glassIntensity === "strong" ? "regular" : "regular"),
        [effect, glassIntensity]
    );

    // Memoize isNoneEffect check
    const isNoneEffect = React.useMemo(
        () => originalEffect === "none" || effect === "none" || resolvedEffect === "none",
        [originalEffect, effect, resolvedEffect]
    );

    // Memoize ref callback (must be before early return)
    const refCallback = React.useCallback((node: HTMLDivElement | null) => {
        // Handle forwarded ref
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            // @ts-ignore - ref.current is mutable in practice
            ref.current = node;
        }
        // Store in cardRef for dimension tracking
        cardRef.current = node;
        // Update dimensions when ref is set using requestAnimationFrame
        if (node) {
            requestAnimationFrame(updateDimensions);
        }
    }, [ref, updateDimensions]);

    // If useSimpleGlass is true, use simple glass effect
    if (useSimpleGlass) {
        // Determine color scheme
        const isDarkSimple = colorScheme === "system"
            ? systemIsDark
            : colorScheme === "dark";

        // Map old glassIntensity to new effect prop for backward compatibility
        const resolvedEffectSimple = effect || (glassIntensity === "light" ? "clear" : glassIntensity === "strong" ? "regular" : "regular");

        // If effect is 'none', return simple Card without glass effect
        if (resolvedEffectSimple === "none") {
            return (
                <Card
                    ref={ref}
                    className={className}
                    sx={{
                        overflow: "hidden",
                        borderRadius: (theme) => theme.shape.borderRadius * 2, // 12px default
                        ...sx,
                    }}
                    {...props}
                >
                    {children}
                </Card>
            );
        }

        // Native liquid glass effect configuration - enhanced for stronger white blur effect
        const glassConfigSimple = {
            clear: {
                blur: "blur(30px) saturate(200%)",
                bgLight: alpha("#ffffff", 0.5),
                bgDark: alpha("#ffffff", 0.3),
                borderLight: alpha("#ffffff", 0.6),
                borderDark: alpha("#ffffff", 0.5),
                shadowLight: "0 8px 32px 0 rgba(31, 38, 135, 0.2)",
                shadowDark: "0 8px 32px 0 rgba(0, 0, 0, 0.5)",
                insetLight: "inset 0 1px 0 0 rgba(255, 255, 255, 0.5)",
                insetDark: "inset 0 1px 0 0 rgba(255, 255, 255, 0.3)",
            },
            regular: {
                blur: "blur(35px) saturate(220%)",
                bgLight: alpha("#ffffff", 0.6),
                bgDark: alpha("#ffffff", 0.4),
                borderLight: alpha("#ffffff", 0.7),
                borderDark: alpha("#ffffff", 0.6),
                shadowLight: "0 8px 32px 0 rgba(31, 38, 135, 0.25)",
                shadowDark: "0 8px 32px 0 rgba(0, 0, 0, 0.6)",
                insetLight: "inset 0 1px 0 0 rgba(255, 255, 255, 0.6)",
                insetDark: "inset 0 1px 0 0 rgba(255, 255, 255, 0.4)",
            },
        };

        const configSimple = glassConfigSimple[resolvedEffectSimple];
        const shouldHoverSimple = hoverable || interactive;

        // For nested cards, use glass effect without backdrop-filter to avoid blurring parent
        if (nested) {
            return (
                <Box
                    ref={ref}
                    className={`glass-effect ${isDarkSimple ? "glass-effect-dark" : ""} glass-transition ${shouldHoverSimple ? "glass-hover" : ""} ${className || ""}`}
                    sx={{
                        position: "relative",
                        borderRadius: (theme) => theme.shape.borderRadius * 2,
                        overflow: "hidden",
                        border: `1px solid ${isDarkSimple ? configSimple.borderDark : configSimple.borderLight}`,
                        // For nested, use more transparent background without backdrop-filter for better liquid glass effect
                        background: isDarkSimple
                            ? `linear-gradient(135deg, ${alpha("#ffffff", 0.08)} 0%, ${alpha("#ffffff", 0.05)} 100%)`
                            : `linear-gradient(135deg, ${alpha("#ffffff", 0.15)} 0%, ${alpha("#ffffff", 0.1)} 100%)`,
                        boxShadow: isDarkSimple ? configSimple.shadowDark : configSimple.shadowLight,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        [theme.breakpoints.down("sm")]: {
                            borderRadius: (theme.shape.borderRadius * 1.5),
                        },
                        // Inset highlight for glass effect
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: (theme) => theme.shape.borderRadius * 2,
                            pointerEvents: "none",
                            boxShadow: isDarkSimple ? configSimple.insetDark : configSimple.insetLight,
                            zIndex: 1,
                            [theme.breakpoints.down("sm")]: {
                                borderRadius: (theme) => theme.shape.borderRadius * 1.5,
                            },
                        },
                        ...(shouldHoverSimple && {
                            cursor: "pointer",
                            "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: isDarkSimple
                                    ? "0 12px 48px 0 rgba(0, 0, 0, 0.6)"
                                    : "0 12px 48px 0 rgba(0, 0, 0, 0.2)",
                                border: `1px solid ${isDarkSimple ? alpha("#ffffff", 0.7) : alpha("#ffffff", 0.8)}`,
                            },
                        }),
                        ...sx,
                    }}
                    {...props}
                >
                    {/* Gradient overlays for glass shine effect - more transparent for nested */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "50%",
                            background: `linear-gradient(180deg, ${alpha("#ffffff", isDarkSimple ? 0.1 : 0.15)} 0%, transparent 100%)`,
                            pointerEvents: "none",
                            zIndex: 1,
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background: `linear-gradient(90deg, transparent 0%, ${alpha("#ffffff", 0.2)} 50%, transparent 100%)`,
                            pointerEvents: "none",
                            zIndex: 1,
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            bottom: 0,
                            right: 0,
                            width: "4px",
                            background: `linear-gradient(180deg, transparent 0%, ${alpha("#ffffff", 0.2)} 50%, transparent 100%)`,
                            pointerEvents: "none",
                            zIndex: 1,
                        }}
                    />
                    {/* Additional white overlay for nested cards */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `linear-gradient(135deg, ${alpha("#ffffff", isDarkSimple ? 0.25 : 0.35)} 0%, transparent 50%, ${alpha("#ffffff", isDarkSimple ? 0.2 : 0.3)} 100%)`,
                            pointerEvents: "none",
                            zIndex: 1,
                        }}
                    />
                    {/* Content */}
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            );
        }

        return (
            <Box
                ref={ref}
                className={`glass-effect ${isDarkSimple ? "glass-effect-dark" : ""} glass-transition ${shouldHoverSimple ? "glass-hover" : ""} ${className || ""}`}
                sx={{
                    position: "relative",
                    borderRadius: (theme) => theme.shape.borderRadius * 2, // 12px default, same as CardBase
                    overflow: "hidden",
                    border: `1px solid ${isDarkSimple ? configSimple.borderDark : configSimple.borderLight}`,
                    background: isDarkSimple ? configSimple.bgDark : configSimple.bgLight,
                    backdropFilter: configSimple.blur,
                    WebkitBackdropFilter: configSimple.blur,
                    boxShadow: isDarkSimple ? configSimple.shadowDark : configSimple.shadowLight,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    [theme.breakpoints.down("sm")]: {
                        borderRadius: (theme.shape.borderRadius * 1.5),
                    },
                    // Inset highlight for glass effect
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: (theme) => theme.shape.borderRadius * 2,
                        pointerEvents: "none",
                        boxShadow: isDarkSimple ? configSimple.insetDark : configSimple.insetLight,
                        zIndex: 1,
                        [theme.breakpoints.down("sm")]: {
                            borderRadius: (theme) => theme.shape.borderRadius * 1.5,
                        },
                    },
                    ...(shouldHoverSimple && {
                        cursor: "pointer",
                        "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: isDarkSimple
                                ? "0 12px 48px 0 rgba(0, 0, 0, 0.6)"
                                : "0 12px 48px 0 rgba(0, 0, 0, 0.2)",
                            border: `1px solid ${isDarkSimple ? alpha("#ffffff", 0.7) : alpha("#ffffff", 0.8)}`,
                        },
                    }),
                    ...sx,
                }}
                {...props}
            >
                {/* Gradient overlays for glass shine effect - enhanced for stronger white effect */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "50%",
                        background: `linear-gradient(180deg, ${alpha("#ffffff", isDarkSimple ? 0.25 : 0.4)} 0%, transparent 100%)`,
                        pointerEvents: "none",
                        zIndex: 1,
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "4px",
                        background: `linear-gradient(90deg, transparent 0%, ${alpha("#ffffff", 0.5)} 50%, transparent 100%)`,
                        pointerEvents: "none",
                        zIndex: 1,
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        right: 0,
                        width: "4px",
                        background: `linear-gradient(180deg, transparent 0%, ${alpha("#ffffff", 0.5)} 50%, transparent 100%)`,
                        pointerEvents: "none",
                        zIndex: 1,
                    }}
                />
                {/* Additional white overlay for non-nested cards */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(135deg, ${alpha("#ffffff", isDarkSimple ? 0.1 : 0.15)} 0%, transparent 50%, ${alpha("#ffffff", isDarkSimple ? 0.05 : 0.1)} 100%)`,
                        pointerEvents: "none",
                        zIndex: 1,
                    }}
                />
                {/* Content */}
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        width: "100%",
                        height: "100%",
                    }}
                >
                    {children}
                </Box>
            </Box>
        );
    }

    // If effect is 'none', return simple Card without glass effect (after all hooks)
    if (isNoneEffect) {
        return (
            <Card
                className={className}
                sx={{
                    overflow: "hidden",
                    borderRadius: (theme) => theme.shape.borderRadius * 2, // 12px default
                    ...sx,
                }}
                {...props}
            >
                {children}
            </Card>
        );
    }

    // For nested cards, use glass effect without backdrop-filter to avoid blurring parent
    if (nested) {
        return (
            <Box
                className={computedClassName}
                sx={nestedSx}
                {...props}
            >
                {/* WebGL Glass Background Effect - Disabled on mobile automatically */}
                {effectiveUseWebGL && (
                    <WebGLGlassBackground
                        fragmentShader={webGLFragmentShader}
                        sourceImage={webGLSourceImage}
                        sx={{
                            zIndex: 1,
                            pointerEvents: "auto",
                        }}
                    />
                )}
                <Box sx={contentBoxSx}>{children}</Box>
            </Box>
        );
    }

    return (
        <>
            {/* SVG Filter Definition with Displacement Map - Only if liquid effect is enabled */}
            {useLiquidEffect && displacementSvg && (
                <SVGFilter filterId={filterId} displacementSvg={displacementSvg} />
            )}
            <Box
                ref={useLiquidEffect ? refCallback : ref}
                className={computedClassName}
                sx={mainSx}
                {...props}
            >
                {/* WebGL Glass Background Effect - Disabled on mobile automatically */}
                {effectiveUseWebGL && (
                    <WebGLGlassBackground
                        fragmentShader={webGLFragmentShader}
                        sourceImage={webGLSourceImage}
                        sx={{
                            zIndex: 1,
                            pointerEvents: "auto",
                        }}
                    />
                )}
                <Box sx={contentBoxSx}>{children}</Box>
            </Box>
        </>
    );
});

GlassCard.displayName = "GlassCard";

export default GlassCard;